import type { SectionTopic } from '../api/client'

/** Editorial Desk: the per-title drafts sections a user may open (spec 2026-09-28 §6). */
export type SectionLink = { slug: string; label: string; path: string; switched_on?: boolean; readers_rides?: boolean }

/** In SECTION_AGENT_PUBS on the server. A set-up title that is not opens
 *  read-only: nothing can be written or published for it yet. */
export const isSwitchedOn = (s: Pick<SectionLink, 'switched_on'>): boolean => s.switched_on !== false

export function sectionForPath(sections: SectionLink[], path: string | undefined): SectionLink | null {
  return sections.find((s) => s.path === path) ?? null
}

/** Where an editor lands: their one title, the chooser when they have several,
 *  or the "no titles" page when none is ticked. */
export function editorialLanding(sections: SectionLink[]): string {
  if (sections.length === 1) return `/editorial/${sections[0].path}`
  return sections.length ? '/editorial' : '/editorial/none'
}

const PIECE_TYPES: Record<string, string> = {
  how_to: 'How-to',
  gear: 'New gear',
  scale_history: 'Scale & history',
  events_community: 'Events & community',
  new_release: 'New release',
  collecting: 'Collecting',
  real_car: 'Real car',
  shows_community: 'Shows & community',
  racing_events: 'Racing & events',
  vintage: 'Vintage',
  rigs_community: 'Rigs & community',
}

export function pieceTypeLabel(key: string | null | undefined): string | null {
  return key ? PIECE_TYPES[key] ?? null : null
}

/** Each title's piece types in display order, for the editor's own-subject
 *  picker. No API exposes them, so this mirrors section_agent/profiles.py.
 *  Flight Journal has none (its subjects carry no type). */
const TITLE_PIECE_TYPES: Record<string, string[]> = {
  model_airplane_news: ['how_to', 'gear', 'scale_history', 'events_community'],
  rc_car_action: ['new_release', 'how_to', 'racing_events', 'vintage', 'rigs_community'],
  diecast: ['new_release', 'collecting', 'real_car', 'shows_community'],
}

export function pieceTypeKeys(publicationSlug: string): string[] {
  return TITLE_PIECE_TYPES[publicationSlug] ?? []
}

/** What draft-now takes: a ranked subject, a failed draft's subject, or
 *  one the editor typed. Only the label is required. */
export type TopicRequest = Pick<SectionTopic, 'label'> & Partial<SectionTopic>

/** The subject to redraft from a failed or rejected draft; undefined when
 *  the run never got as far as choosing one (a scheduled run stores the
 *  label '(auto)'), which draft-now reads as "the best open subject". */
export function retryTopic(topic: { label?: string } | null | undefined): TopicRequest | undefined {
  const label = topic?.label?.trim()
  if (!label || label === '(auto)') return undefined
  return { ...topic, label } as TopicRequest
}

/** A subject the editor typed: no search data, so the label doubles as the
 *  search the piece answers. Undefined until there is a label. */
export function customTopic(label: string, intent: string, pieceType: string | null): TopicRequest | undefined {
  const l = label.trim()
  if (!l) return undefined
  return {
    label: l, search_intent: intent.trim(), representative_query: l, queries: [],
    ...(pieceType ? { piece_type: pieceType } : {}),
  }
}
